/**
 * MonPlanningScolairePage — planning de l'agent scolaire / cantine.
 *
 * 24/07 — bascule sur le composant PARTAGÉ `PresenceSimplePage`, comme
 * l'urbanisme, la comptabilité et la petite enfance. Auparavant cette page
 * portait son propre rendu (liste de cartes par jour) : le planning s'affichait
 * donc différemment selon la brique, pour la même fonction.
 *
 * ── Pourquoi un adaptateur ──────────────────────────────────────────────────
 *
 * L'API scolaire expose bien les 15 méthodes du contrat, mais avec des types
 * PLUS STRICTS que le contrat commun (le scolaire impose un `type` de créneau
 * et de profil, et son créneau ne porte pas `profilId`). Plutôt que d'affaiblir
 * les types du scolaire — ou de dupliquer le composant — on comble l'écart ici,
 * au seul point de contact.
 */
import { api } from '@/lib/api';
import { ScolaireLayout } from '@/components/scolaire/ScolaireLayout';
import { PresenceSimplePage } from '@/pages/shared/PresenceSimplePage';
import type { ProfilPlage, ScolairePlanningType } from '@roronoa-collectivites/shared-types';

const TYPE_DEFAUT: ScolairePlanningType = 'cantine';

/** Complète les champs que le contrat commun attend et que le scolaire n'expose pas. */
function versGrille<T extends object>(c: T) {
  return { ...c, profilId: null } as T & { profilId: null };
}

export function MonPlanningScolairePage() {
  const p = api.scolaire.planning;

  return (
    <ScolaireLayout activeTab="planning">
      <PresenceSimplePage
        planningApi={{
          ...p,
          getWeek: (from: string, to: string) => p.getWeek(from, to).then((cs) => cs.map(versGrille)),
          getMaSemaine: (from: string, to: string) => p.getMaSemaine(from, to).then((cs) => cs.map(versGrille)),
          createCreneau: (body) =>
            p.createCreneau({
              agentUserId: body.agentUserId,
              dateDebut: body.dateDebut,
              dateFin: body.dateFin,
              titre: body.titre,
              description: body.description,
              type: (body.type ?? TYPE_DEFAUT) as ScolairePlanningType,
              couleurMarker: body.couleurMarker ?? undefined,
            }).then(versGrille),
          updateCreneau: (id, body) => p.updateCreneau(id, body).then(versGrille),
          createProfil: (body: { nom: string; type?: string; plages: ProfilPlage[] }) =>
            p.createProfil({
              nom: body.nom,
              type: (body.type ?? TYPE_DEFAUT) as ScolairePlanningType,
              plages: body.plages,
            }),
        }}
        accent="scolaire"
        service="scolaire_cantine"
      />
    </ScolaireLayout>
  );
}
